import { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import QuestionManagement from './QuestionManagement';
import QuestionDrafting from './QuestionDrafting';
import GameControl from './GameControl';
import OngoingGames from './OngoingGames';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { authService } from '@/services/auth';
import { empiricalStudyService } from '@/services/study/empiricalStudyService';
import {
  buildEmpiricalStudyCsv,
  downloadEmpiricalStudyCsv
} from '@/utils/empiricalStudyExport';
import type { EmpiricalStudyParticipantRecord, GameSession } from '@/types';

const NAV_TABS = [
  { id: 'overview', label: '📊 Overview' as const },
  { id: 'questions', label: '📚 Questions' as const },
  { id: 'control', label: '🎮 Game Control' as const },
  { id: 'ongoing', label: '🏃 Ongoing Games' as const },
  { id: 'analytics', label: '📈 Analytics' as const }
] as const;

type DashboardSection = typeof NAV_TABS[number]['id'];

const formatDateTime = (date?: Date) => {
  if (!date) return 'Not available';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
};

const getStudySessionMoment = (session: GameSession): Date => {
  return session.startedAt || session.completedAt || session.createdAt;
};

const buildStudyExportFileName = (session: GameSession): string => {
  const safeGameName = session.questionSetName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  const safeDate = getStudySessionMoment(session).toISOString().slice(0, 16).replace(/[:T]/g, '-');
  return `homerun-study-${safeGameName || session.gameCode}-${safeDate}.csv`;
};

const TeacherDashboard = () => {
  const navigate = useSafeNavigate();
  const { user, teacherProfile, signOut } = useAuthStore();
  const [activeSection, setActiveSection] = useState<DashboardSection>('overview');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [editingQuestionSetId, setEditingQuestionSetId] = useState<string | null>(null);
  const [ongoingGamesRefreshKey, setOngoingGamesRefreshKey] = useState(0);
  const [studySessions, setStudySessions] = useState<GameSession[]>([]);
  const [studyRecords, setStudyRecords] = useState<EmpiricalStudyParticipantRecord[]>([]);
  const [isLoadingStudyAnalytics, setIsLoadingStudyAnalytics] = useState(false);
  const [studyAnalyticsError, setStudyAnalyticsError] = useState<string | null>(null);
  const [exportingStudySessionId, setExportingStudySessionId] = useState<string | null>(null);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/lobby');
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return 'Unknown';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  const handleNavigateToQuestionEditor = (questionSetId?: string) => {
    setEditingQuestionSetId(questionSetId || 'new');
    setActiveSection('questions');
    console.log('🚀 Navigate to question editor for set:', questionSetId || 'new');
  };

  const handleBackToQuestionManagement = () => {
    setEditingQuestionSetId(null);
  };

  const handleRefreshOngoingGames = () => {
    console.log('🔄 Refreshing ongoing games list');
    setOngoingGamesRefreshKey(prev => prev + 1);
  };

  useEffect(() => {
    if (activeSection !== 'analytics' || !user?.uid) {
      return;
    }

    let isCancelled = false;

    const loadStudyAnalytics = async () => {
      try {
        setIsLoadingStudyAnalytics(true);
        setStudyAnalyticsError(null);

        const [sessions, records] = await Promise.all([
          authService.getTeacherGameSessions(user.uid),
          empiricalStudyService.getParticipantRecordsForTeacher(user.uid)
        ]);

        if (isCancelled) {
          return;
        }

        const empiricalSessions = sessions
          .filter((session) => session.empiricalStudyEnabled)
          .sort((a, b) => getStudySessionMoment(b).getTime() - getStudySessionMoment(a).getTime());

        setStudySessions(empiricalSessions);
        setStudyRecords(records);
      } catch (analyticsError) {
        if (isCancelled) {
          return;
        }

        console.error('Failed to load study analytics:', analyticsError);
        setStudyAnalyticsError(
          analyticsError instanceof Error ? analyticsError.message : 'Unknown error'
        );
      } finally {
        if (!isCancelled) {
          setIsLoadingStudyAnalytics(false);
        }
      }
    };

    loadStudyAnalytics();

    return () => {
      isCancelled = true;
    };
  }, [activeSection, user?.uid]);

  const handleDownloadStudySessionData = async (session: GameSession) => {
    const recordsForSession = studyRecords.filter((record) => record.sessionId === session.id);

    if (recordsForSession.length === 0) {
      alert('No empirical study records were found for this game yet.');
      return;
    }

    try {
      setExportingStudySessionId(session.id);
      const csv = buildEmpiricalStudyCsv(recordsForSession);
      downloadEmpiricalStudyCsv(csv, buildStudyExportFileName(session));
    } catch (exportError) {
      console.error('Failed to export study data:', exportError);
      alert(
        `Failed to export study data: ${
          exportError instanceof Error ? exportError.message : 'Unknown error'
        }`
      );
    } finally {
      setExportingStudySessionId(null);
    }
  };

  const studyRecordCountBySession = studyRecords.reduce<Record<string, number>>((counts, record) => {
    counts[record.sessionId] = (counts[record.sessionId] || 0) + 1;
    return counts;
  }, {});

  const studyConsentCountBySession = studyRecords.reduce<Record<string, number>>((counts, record) => {
    if (record.consentChoice === 'consent') {
      counts[record.sessionId] = (counts[record.sessionId] || 0) + 1;
    }
    return counts;
  }, {});

  const renderStudyAnalyticsContent = () => {
    if (isLoadingStudyAnalytics) {
      return (
        <div style={{
          background: 'white',
          borderRadius: '20px',
          border: '1px solid #e5e7eb',
          padding: '2rem',
          textAlign: 'center',
          color: '#64748b'
        }}>
          Loading empirical study analytics...
        </div>
      );
    }

    if (studyAnalyticsError) {
      return (
        <div style={{
          background: '#fff7ed',
          borderRadius: '20px',
          border: '1px solid #fdba74',
          padding: '1.5rem',
          color: '#9a3412'
        }}>
          Failed to load empirical study analytics: {studyAnalyticsError}
        </div>
      );
    }

    if (studySessions.length === 0) {
      return (
        <div style={{
          background: 'white',
          borderRadius: '20px',
          border: '1px solid #e5e7eb',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <h3 style={{ color: '#374151', marginTop: 0 }}>No empirical study sessions yet</h3>
          <p style={{ color: '#64748b', marginBottom: 0 }}>
            Once you host a study-enabled game, it will appear here as its own analytics entry with
            a separate game name, date, time, and CSV download.
          </p>
        </div>
      );
    }

    return (
      <div style={{
        display: 'grid',
        gap: '1rem',
        maxWidth: '920px',
        margin: '0 auto'
      }}>
        {studySessions.map((session) => {
          const recordCount = studyRecordCountBySession[session.id] || 0;
          const consentCount = studyConsentCountBySession[session.id] || 0;
          const isExportingThisSession = exportingStudySessionId === session.id;

          return (
            <div
              key={session.id}
              style={{
                background: 'white',
                borderRadius: '20px',
                border: '1px solid #e5e7eb',
                padding: '1.5rem',
                boxShadow: '0 4px 16px rgba(15, 23, 42, 0.06)'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '1rem',
                flexWrap: 'wrap',
                marginBottom: '1rem'
              }}>
                <div>
                  <h3 style={{ color: '#111827', marginTop: 0, marginBottom: '0.35rem' }}>
                    {session.questionSetName}
                  </h3>
                  <div style={{ color: '#64748b', fontSize: '0.95rem' }}>
                    Game code: <strong>{session.gameCode}</strong>
                  </div>
                </div>

                <button
                  onClick={() => handleDownloadStudySessionData(session)}
                  disabled={recordCount === 0 || isExportingThisSession}
                  style={{
                    padding: '0.9rem 1.35rem',
                    background: recordCount === 0 || isExportingThisSession
                      ? '#cbd5e1'
                      : 'linear-gradient(135deg, #f97316, #ea580c)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '999px',
                    cursor: recordCount === 0 || isExportingThisSession ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.95rem'
                  }}
                >
                  {isExportingThisSession ? 'Preparing CSV...' : '⬇️ Download CSV'}
                </button>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '0.9rem',
                marginBottom: '1rem'
              }}>
                <div>
                  <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                    Hosted
                  </div>
                  <div style={{ color: '#334155', fontWeight: 600 }}>
                    {formatDateTime(session.createdAt)}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                    Played
                  </div>
                  <div style={{ color: '#334155', fontWeight: 600 }}>
                    {session.startedAt ? formatDateTime(session.startedAt) : 'Not started'}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                    Study records
                  </div>
                  <div style={{ color: '#334155', fontWeight: 600 }}>{recordCount}</div>
                </div>
                <div>
                  <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                    Consent given
                  </div>
                  <div style={{ color: '#334155', fontWeight: 600 }}>{consentCount}</div>
                </div>
              </div>

              <div style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.5 }}>
                Each hosted run appears separately here, so repeated plays of the same game remain
                different analytics entries.
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      position: 'relative'
    }}>
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)
        `,
        animation: 'float 8s ease-in-out infinite'
      }} />

      {/* Header */}
      <header style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        padding: '1.5rem 2rem',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {/* Logo and Title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{
              width: '50px',
              height: '50px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem'
            }}>
              🎓
            </div>
            <div>
              <h1 style={{
                margin: 0,
                fontSize: '1.8rem',
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent'
              }}>
                Teacher Dashboard
              </h1>
              <p style={{
                margin: 0,
                color: '#64748b',
                fontSize: '0.9rem'
              }}>
                Welcome back, {teacherProfile?.displayName || user?.displayName || 'Teacher'}
              </p>
            </div>
          </div>

          {/* User Profile */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem 1rem',
                background: 'rgba(102, 126, 234, 0.1)',
                border: '2px solid rgba(102, 126, 234, 0.2)',
                borderRadius: '50px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(102, 126, 234, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.2)';
              }}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: user?.photoURL ? `url(${user.photoURL})` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '1.2rem',
                fontWeight: 'bold'
              }}>
                {!user?.photoURL && (teacherProfile?.displayName?.[0] || user?.displayName?.[0] || '👨‍🏫')}
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  {teacherProfile?.displayName || user?.displayName}
                </div>
                <div style={{
                  fontSize: '0.75rem',
                  color: '#64748b'
                }}>
                  {teacherProfile?.institution || 'Teacher'}
                </div>
              </div>
              <div style={{
                fontSize: '0.8rem',
                color: '#64748b',
                transform: showProfileMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </div>
            </button>

            {/* Profile Dropdown */}
            {showProfileMenu && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '0.5rem',
                background: 'white',
                borderRadius: '16px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                minWidth: '280px',
                padding: '1rem',
                zIndex: 50
              }}>
                <div style={{
                  borderBottom: '1px solid #e5e7eb',
                  paddingBottom: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.25rem'
                  }}>
                    {user?.email}
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#64748b'
                  }}>
                    Member since {formatDate(teacherProfile?.createdAt)}
                  </div>
                </div>

                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    // TODO: Navigate to profile settings
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'none',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    color: '#374151',
                    fontSize: '0.9rem',
                    marginBottom: '0.5rem',
                    transition: 'background 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                  }}
                >
                  ⚙️ Profile Settings
                </button>

                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    handleSignOut();
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'none',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    color: '#dc2626',
                    fontSize: '0.9rem',
                    transition: 'background 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#fef2f2';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                  }}
                >
                  🚪 Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{
        padding: '2rem',
        maxWidth: '1400px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 5
      }}>
        {/* Navigation Tabs */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '2rem',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          padding: '0.5rem',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}>
          {NAV_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveSection(tab.id);
                // Refresh ongoing games when switching to that tab
                if (tab.id === 'ongoing') {
                  handleRefreshOngoingGames();
                }
              }}
              style={{
                flex: 1,
                padding: '1rem 1.5rem',
                background: activeSection === tab.id
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'transparent',
                color: activeSection === tab.id ? 'white' : '#64748b',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '1rem',
                transition: 'all 0.2s ease',
                boxShadow: activeSection === tab.id ? '0 4px 15px rgba(102, 126, 234, 0.4)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (activeSection !== tab.id) {
                  e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)';
                  e.currentTarget.style.color = '#667eea';
                }
              }}
              onMouseLeave={(e) => {
                if (activeSection !== tab.id) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#64748b';
                }
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Sections */}
        {activeSection === 'overview' && (
          <div>
            {/* Quick Actions */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              padding: '2rem',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1.5rem'
              }}>
                {[
                  {
                    title: 'Create Question Set',
                    description: 'Start building a new set of questions',
                    icon: '➕',
                    color: '#10b981',
                    action: () => handleNavigateToQuestionEditor()
                  },
                  {
                    title: 'Start Game Session',
                    description: 'Launch a live game with your students',
                    icon: '🚀',
                    color: '#3b82f6',
                    action: () => setActiveSection('control')
                  },
                  {
                    title: 'View Analytics',
                    description: 'Check student performance and progress',
                    icon: '📈',
                    color: '#f59e0b',
                    action: () => setActiveSection('analytics')
                  }
                ].map((action, index) => (
                  <button
                    key={index}
                    onClick={action.action}
                    style={{
                      padding: '1.5rem',
                      background: 'white',
                      border: `2px solid ${action.color}20`,
                      borderRadius: '16px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                      e.currentTarget.style.borderColor = action.color;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.05)';
                      e.currentTarget.style.borderColor = `${action.color}20`;
                    }}
                  >
                    <div style={{
                      fontSize: '2rem',
                      marginBottom: '0.75rem'
                    }}>
                      {action.icon}
                    </div>
                    <div style={{
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      {action.title}
                    </div>
                    <div style={{
                      fontSize: '0.9rem',
                      color: '#64748b',
                      lineHeight: '1.4'
                    }}>
                      {action.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Question Management Section */}
        {activeSection === 'questions' && !editingQuestionSetId && (
          <QuestionManagement onNavigateToEditor={handleNavigateToQuestionEditor} />
        )}

        {/* Question Drafting Section */}
        {activeSection === 'questions' && editingQuestionSetId && (
          <QuestionDrafting
            questionSetId={editingQuestionSetId === 'new' ? null : editingQuestionSetId}
            onBack={handleBackToQuestionManagement}
          />
        )}

        {activeSection === 'control' && (
          <GameControl
            onCreateSession={() => {
              // TODO: Implement session creation wizard
              console.log('Navigate to session creation wizard');
            }}
            onAiGameCreated={handleRefreshOngoingGames}
          />
        )}

        {activeSection === 'ongoing' && (
          <OngoingGames refreshKey={ongoingGamesRefreshKey} />
        )}

        {activeSection === 'analytics' && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            padding: '3rem',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem', textAlign: 'center' }}>📈</div>
            <h2 style={{ color: '#374151', marginBottom: '1rem', textAlign: 'center' }}>
              Student Analytics
            </h2>
            <p style={{ color: '#64748b', marginBottom: '2rem', textAlign: 'center' }}>
              Each study-enabled hosted game appears here separately, with its own game name, date,
              time, and CSV export.
            </p>
            {renderStudyAnalyticsContent()}
          </div>
        )}
      </main>

      {/* Back to Lobby Button */}
      <button
        onClick={() => navigate('/lobby')}
        style={{
          position: 'fixed',
          bottom: '2rem',
          left: '2rem',
          padding: '1rem 1.5rem',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '50px',
          color: '#667eea',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          fontSize: '0.9rem',
          zIndex: 20,
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.1)';
        }}
      >
        ← Back to Lobby
      </button>

      {/* Background Effects */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-15px) rotate(2deg); }
          66% { transform: translateY(10px) rotate(-2deg); }
        }
      `}</style>
    </div>
  );
};

export default TeacherDashboard;
